package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Which way a figure has moved since whatever the caller is comparing it against.
 *
 * Text and direction, never a computed percentage: the comparison period is the caller's decision
 * and "+12 this week" and "12% up" are different claims. [LStatTile] draws the glyph and prints the
 * words; it does no arithmetic of its own.
 */
data class LStatDelta(
    val text: String,
    val direction: LStatDirection = LStatDirection.FLAT,
)

enum class LStatDirection { UP, DOWN, FLAT }

/**
 * One number a page is about, with the word that says what it counts.
 *
 * **This replaces four private copies of itself** — `SummaryTile` on the dashboard, `Count` on the
 * data page, `CountCard` on pre-translate, `CounterBody` in the readiness row — which had drifted
 * into four type sizes, three weights and two different opinions about what a zero looks like. The
 * figure is the loudest thing on most of these surfaces, so four of them disagreeing is four
 * different claims about how important a count is.
 *
 * **A zero is drawn quietly, whatever else is set.** An empty queue is the good outcome, and giving
 * it the weight of a problem makes a finished workspace look as busy as a broken one. That rule
 * beats [tone] and beats [emphasis]: a red 0 is a warning about nothing.
 *
 * **The whole tile is the control, or none of it is.** Given [href] or [onClick] the root element
 * *is* the anchor or the button — not a card containing one — because [LCardStyle]'s lift only
 * reaches a card that is itself the control, and because a link inside a surface that also looks
 * pressable gives the reader two things to aim at and one that works.
 *
 * **The painted figure is `aria-hidden`, and a text twin carries the meaning.** "37" beside "QA
 * ERRORS" is two boxes to the eye and two unrelated announcements to a screen reader, and in a
 * grid of six tiles the second one is unusable. The twin says "QA errors: 37" once.
 */
@Composable
fun LStatTile(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    /** A glyph beside the label, at [IconSize.MD]. Never instead of the label. */
    icon: (@Composable () -> Unit)? = null,
    delta: LStatDelta? = null,
    /**
     * The verdict the figure carries, on the same six-value scale [LBadge] uses.
     *
     * Only read when [emphasis] is set. A tone on every tile is six accents on one row, which is
     * none: hard rule 2 says a page gets one loud thing, and on a row of counters that is the one
     * count that can block a release.
     */
    tone: LBadgeVariant = LBadgeVariant.NEUTRAL,
    href: String? = null,
    onClick: (() -> Unit)? = null,
    /** This is the tile that matters on this row. Paints the figure in [tone]. */
    emphasis: Boolean = false,
    /**
     * The tile without its card: figure first, word under it, no border and no padding.
     *
     * For a row inside something that is already a surface — the readiness band, a queue header —
     * where a second card edge around each count draws five boxes and no hierarchy. The type drops
     * one step with it, because a 33px figure in a header is a heading competing with the heading.
     */
    compact: Boolean = false,
) {
    val palette = ColorMode.current.toSitePalette()
    val zero = value.readsAsZero()
    val figureColor = when {
        zero -> palette.textSecondary
        emphasis -> tone.toneColor(palette)
        else -> palette.textPrimary
    }
    val twin = if (delta == null) {
        tf(
            "ds.ds.lstat_tile.label_value",
            "{label}: {value}",
            "label" to label,
            "value" to value,
        )
    } else {
        tf(
            "ds.ds.lstat_tile.label_value_delta",
            "{label}: {value}, {delta}",
            "label" to label,
            "value" to value,
            "delta" to delta.text,
        )
    }

    val root = LStatTileStyle.toModifier()
        .thenIf(!compact) { LCardStyle.toModifier().width(100.percent) }
        // A `<button>` arrives with a ground, an edge and padding from the browser. A card supplies
        // its own; a compact tile has to say it wants none.
        .thenIf(compact) {
            Modifier.styleModifier {
                property("background", "transparent")
                property("border", "0")
                property("padding", "0")
            }
        }
        .then(modifier)

    val body: @Composable () -> Unit = {
        if (compact) {
            StatFigure(value, figureColor, compact = true)
            StatLabel(label, icon, palette, compact = true)
        } else {
            StatLabel(label, icon, palette, compact = false)
            StatFigure(value, figureColor, compact = false)
        }
        if (delta != null) StatDelta(delta)
        Span(LVisuallyHiddenStyle.toModifier().toAttrs()) { Text(twin) }
    }

    when {
        href != null -> Link(
            path = href,
            // Silk paints links; this one inherits its colour so the quiet-zero rule above still
            // shows through, exactly as the readiness band's plain link did.
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            modifier = root,
        ) { body() }

        onClick != null -> {
            // Bound to a local before the attribute lambda: inside `toAttrs` the name `onClick`
            // also belongs to `AttrsScope`, and a reader should not have to work out which of the
            // two each occurrence means.
            val press: () -> Unit = onClick
            Button(
                attrs = root.toAttrs {
                    type(ButtonType.Button)
                    onClick { press() }
                },
            ) { body() }
        }

        else -> Span(root.toAttrs()) { body() }
    }
}

@Composable
private fun StatLabel(
    label: String,
    icon: (@Composable () -> Unit)?,
    palette: SitePalette,
    compact: Boolean,
) {
    Span(
        LStatTileLabelStyle.toModifier()
            .thenIf(compact) {
                // A compact label sits under its own figure and reads as a caption, not as a
                // column head, so it keeps sentence case and the body scale.
                Modifier
                    .fontSize(TypeScale.Small)
                    .fontWeight(400)
                    .styleModifier {
                        property("text-transform", "none")
                        property("letter-spacing", "normal")
                    }
            }
            .toAttrs { attr("aria-hidden", "true") },
    ) {
        if (icon != null) {
            Span(
                Modifier
                    .iconSize(IconSize.MD)
                    .color(palette.textSecondary)
                    .styleModifier {
                        property("display", "inline-flex")
                        property("flex", "0 0 auto")
                    }
                    .toAttrs { attr("data-stat-icon", "") },
            ) { icon() }
        }
        Text(label)
    }
}

@Composable
private fun StatFigure(value: String, figureColor: Color, compact: Boolean) {
    Span(
        LStatTileValueStyle.toModifier()
            .color(figureColor)
            .thenIf(compact) { Modifier.fontSize(TypeScale.HeadingSmall) }
            .toAttrs { attr("aria-hidden", "true") },
    ) {
        Text(value)
    }
}

@Composable
private fun StatDelta(delta: LStatDelta) {
    Span(LStatTileDeltaStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
        // The glyph, not a colour. Up is not automatically good — more untranslated strings is a
        // rise nobody wants — so the direction is drawn as a shape and the words say what it means.
        when (delta.direction) {
            LStatDirection.UP -> LTrendingUpIcon(Modifier.iconSize(IconSize.SM))
            LStatDirection.DOWN -> LTrendingDownIcon(Modifier.iconSize(IconSize.SM))
            LStatDirection.FLAT -> Unit
        }
        Text(delta.text)
    }
}

/**
 * The tile's own layout, and everything a `<button>` or an `<a>` has to be told to stop doing.
 *
 * No padding, background or border here: a full tile gets those from [LCardStyle], which is
 * registered before this style and so is overridden by anything this one sets. Setting them twice
 * is how a card ends up with two paddings and the second one wins by accident.
 */
val LStatTileStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", Space.XXS.toString())
                property("text-align", "start")
                property("text-decoration", "none")
                // A `<button>` does not inherit type. Without this a tile rendered as a button is
                // set in the UA's 13px system face and every other tile on the row is not.
                property("font", "inherit")
                property("-webkit-appearance", "none")
                property("appearance", "none")
                property("align-items", "flex-start")
            }
            .color(ColorTokens.TextPrimary.value())
    }
    cssRule(":is(a, button)") {
        Modifier.cursor(Cursor.Pointer)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val LStatTileLabelStyle = CssStyle {
    base {
        Modifier
            .color(ColorTokens.TextSecondary.value())
            .fontSize(TypeScale.Micro)
            .fontWeight(600)
            .lineHeight(1)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", Space.XS.toString())
                property("letter-spacing", TypeScale.MicroTracking.toString())
                property("text-transform", "uppercase")
            }
    }
    // The glyph fills the square the slot reserves rather than the other way round. `LIcon` draws
    // itself at [IconSize.XL] unless a call site says otherwise, and a slot is 16 — without this a
    // tile's mark overflows its own box and shoulders the word sideways.
    cssRule(" [data-stat-icon] > svg") {
        Modifier.width(100.percent).height(100.percent)
    }
}

val LStatTileValueStyle = CssStyle.base {
    Modifier
        .displayFont()
        .tabularNumbers()
        .fontSize(TypeScale.Heading)
        .fontWeight(700)
        .lineHeight(TypeScale.DisplayLineHeight)
        // The figure is the one thing on this surface that changes under the reader's eye — a
        // pre-translation run repolls and the count drops. Colour only, and never the number
        // itself: hard rule 5 says numbers do not animate, so nothing counts up to the new value.
        .transitionColors(durationMs = Motion.StandardMs)
}

val LStatTileDeltaStyle = CssStyle.base {
    Modifier
        .color(ColorTokens.TextSecondary.value())
        .fontSize(TypeScale.Small)
        .tabularNumbers()
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", Space.XXS.toString())
            property("line-height", TypeScale.BodyLineHeight.toString())
        }
}
